import type { SubscriptionTier } from '@/hooks/useSubscription';

/**
 * Tier hierarchy (higher number = more access):
 *   stylist (1) → pro_stylist (2) → pro_competitor (3) → vip (4)
 */
export const TIER_RANK: Record<string, number> = {
  stylist: 1,
  pro_stylist: 2,
  pro_competitor: 3,
  vip: 4,
};

/** Human-readable labels for each tier */
export const TIER_LABELS: Record<string, string> = {
  stylist: 'Stylist',
  pro_stylist: 'Pro Stylist',
  pro_competitor: 'Pro Competitor',
  vip: 'All Access VIP',
};

/**
 * Route → minimum tier mapping.
 * Routes not listed here either require auth-only or are public.
 *
 * TIER LADDER:
 * ─────────────────────────────────────────────
 * Stylist          → Ask, Saved, Client Records
 * Pro Stylist      → + Certifications, Challenge Board, Hair Rescue
 * Pro Competitor   → + Outsmart the AI Arena, Transformation Challenge, Voting, Money Zone
 * All Access VIP   → + Zoom sessions, tutoring, premium support
 * ─────────────────────────────────────────────
 * Salon Portal is intentionally NOT included in any tier —
 * it uses its own separate licensing system.
 */
export const ROUTE_MIN_TIER: Record<string, SubscriptionTier> = {
  // ── Stylist tier (Hair Pro AI Stylist — $17/wk or $47/mo) ──
  '/ask': 'stylist',
  '/answer': 'stylist',
  '/saved': 'stylist',
  '/client-records': 'stylist',
  '/assistant': 'stylist',
  '/pro-decision': 'stylist',
  '/bro-check': 'stylist',
  '/hug-finish': 'stylist',
  '/keep-lifting': 'stylist',
  '/finish-decision': 'stylist',
  '/ice-finish': 'stylist',
  '/perm-method': 'stylist',
  // The Books™ is included in Hair Pro AI Stylist
  '/books': 'stylist',
  '/books/budget': 'stylist',
  '/books/goals': 'stylist',
  '/books/dopamine': 'stylist',
  '/books/money-moments': 'stylist',
  '/books/growth': 'stylist',

  // ── Pro Stylist tier ($97/mo — Booking, Scheduling, Deposits, Forms, Reminders) ──
  '/dashboard/bookings': 'pro_stylist',

  // ── Pro Stylist tier ──
  // '/certifications' is now public (free quiz access for everyone)
  '/challenge-board': 'pro_stylist',
  '/hair-rescue': 'pro_stylist',

  // ── Pro Competitor tier ──
  '/fix-it': 'pro_competitor',
  '/fix-it-challenge': 'pro_competitor',
  '/fix-it-arena': 'pro_competitor',
  '/fix-it-battle': 'pro_competitor',        // prefix-matched for :battleId
  '/transformation-challenge': 'pro_competitor',
  '/money-zone': 'pro_competitor',
  '/competition-entries': 'pro_competitor',
  '/model-release-form': 'pro_competitor',

  // ── All Access VIP tier ──
  // Zoom sessions, tutoring, and premium support routes
  // (added here as they're built)
};

/**
 * Resolve the minimum tier required for a given pathname.
 * Returns null if no tier restriction applies (public or auth-only).
 */
export function getMinTierForRoute(pathname: string): SubscriptionTier {
  // Exact match first
  if (ROUTE_MIN_TIER[pathname]) return ROUTE_MIN_TIER[pathname];

  // Prefix match for dynamic routes like /fix-it-battle/:id
  for (const [route, tier] of Object.entries(ROUTE_MIN_TIER)) {
    if (pathname.startsWith(route + '/')) return tier;
  }

  return null;
}

/** Check if a user's tier meets the minimum requirement */
export function meetsMinTier(
  userTier: SubscriptionTier,
  requiredTier: SubscriptionTier
): boolean {
  if (!requiredTier) return true;
  if (!userTier) return false;
  return (TIER_RANK[userTier] ?? 0) >= (TIER_RANK[requiredTier] ?? 0);
}
